import React from "react";
import TableChartIcon from "@mui/icons-material/TableChart";
import JoinRightIcon from "@mui/icons-material/JoinRight";
import MessageIcon from "@mui/icons-material/Message";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { AgentLogo } from "components/KBo/Branding/AgentLogo";
import AcxMenu, {
    AcxMenuItemProps,
    StyledMenuLabel,
} from "components/UI/Menu/AcxMenu";
import {
    rowHasChildren,
    SignalsEndpoints,
    SignalsTemporalLabels,
} from "components/Signals/SignalsModels";
import { useNavigate } from "react-router";
import BaseObjectStore from "../BaseObjectStore";
import PointMenuItemHeader from "./PointMenuItemHeader";
import { Moment } from "moment";
import { DrilldownSource, ZoomPathEntry } from "./DrilldownTypes";
import {
    resolvePointSelectActions,
    type PointSelectActions,
} from "../_DataEndpointConfig/Types";

const PERCENT_REMAINDER_SERIES_LABEL = "Other";
const PERCENT_REMAINDER_POINT_SELECT_ACTIONS: PointSelectActions = {
    askKbo: false,
    viewInConvos: false,
    viewTopics: false,
    viewDrivers: false,
    viewData: false,
    viewLevel: false,
};

type Props = {
    store: BaseObjectStore<any, any> & DrilldownSource;
    open: boolean;
    anchorPosition?: { top: number; left: number };
    onClose: () => void;
    isPercentage?: boolean;
    isTemporal?: boolean;
    temporalData?: {
        temporalGrain: SignalsTemporalLabels;
        startDate: Moment | undefined;
        endDate: Moment | undefined;
    };
    /** Renders the menu header as a bare label. See PointMenuItemHeader's `hideValue`. */
    hideSelectionValue?: boolean;
    pointTooltip?: React.ReactElement;
};

type ZoomCapable = {
    zoomStore?: {
        currentDepth: number;
        displayedZoomPath: ZoomPathEntry[];
        zoomInto: (entry: Omit<ZoomPathEntry, "depth">) => Promise<void>;
    };
};

const PointMenu: React.FC<Props> = ({
    store,
    open,
    anchorPosition,
    onClose,
    isPercentage,
    isTemporal,
    temporalData,
    hideSelectionValue,
    pointTooltip,
}) => {
    const navigate = useNavigate();

    if (!store.config.capabilities.pointSelectMenu || !store.drilldownStore)
        return null;
    const drilldownStore = store.drilldownStore;
    const selectedPoint = drilldownStore.selectedDataPoint;
    const selectedTopic = drilldownStore.selectedTopic;
    const selectedDriver = drilldownStore.selectedDriver;
    const selectedPayload = selectedPoint ?? selectedTopic ?? selectedDriver;
    const isPercentMode = isPercentage ?? false;
    const zoomCapable = store as unknown as ZoomCapable;
    const configuredPointSelectActions = resolvePointSelectActions(
        store.config,
        zoomCapable.zoomStore?.displayedZoomPath.length,
    );
    const effectivePointSelectActions =
        store.config.percentRemainderSeries &&
        selectedPoint?.series === PERCENT_REMAINDER_SERIES_LABEL
        ? PERCENT_REMAINDER_POINT_SELECT_ACTIONS
        : configuredPointSelectActions;

    const allowPointSelectMenu = store.config.capabilities.pointSelectMenu;

    if (!allowPointSelectMenu) return null;

    if (!selectedPayload) return null;

    const menuItems: ("Divider" | AcxMenuItemProps)[] = [
        {
            id: `${store.id}-point-menu-header`,
            label: (
                <PointMenuItemHeader
                    isTemporal={isTemporal ?? false}
                    selectedPayload={selectedPayload}
                    isPercentage={isPercentMode}
                    temporalData={temporalData}
                    preferSeries={
                        store.config.renderOptions.preferSeriesInTooltip
                    }
                    hideValue={hideSelectionValue}
                    pointTooltip={selectedPoint ? pointTooltip : undefined}
                />
            ),
            props: { disabled: true, style: { opacity: 1 } },
        },
        "Divider",
    ];

    // Zoom point action ("View {nextLevelLabel}") — availability is gated by:
    //   - the endpoint config opting in via pointSelectActions.viewLevel
    //   - the store carrying a ZoomStore (chart cards do; small/text cards
    //     don't — soft cast through a narrow shape rather than tightening the
    //     PointMenu store-type signature for one chart type). The action stays
    //     visible for leaf rows so the user can see why it is unavailable.
    if (
        effectivePointSelectActions.viewLevel &&
        selectedPoint &&
        zoomCapable.zoomStore
    ) {
        const nextDepth = zoomCapable.zoomStore.currentDepth + 1;
        const nextLevelLabel =
            store.parsedVizOptions?.levelLabels?.[nextDepth] ?? "Next Level";
        const hasNextLevel = rowHasChildren(selectedPoint.metadata);
        const pendoId =
            store.dataEndpoint === SignalsEndpoints.drivers.TopTwentyThemesID
                ? nextDepth === 1
                    ? "top-themes-view-drivers"
                    : nextDepth === 2
                    ? "top-themes-view-drivers-related-drivers"
                    : undefined
                : undefined;
        menuItems.push({
            id: pendoId ?? `${store.id}-point-view-level`,
            label: (
                <StyledMenuLabel id="view-level">
                    View {nextLevelLabel}
                </StyledMenuLabel>
            ),
            props: {
                disabled: !hasNextLevel,
                onClick: hasNextLevel
                    ? () => {
                          onClose();
                          void zoomCapable.zoomStore!.zoomInto({
                              x:
                                  selectedPoint.xLabel ??
                                  String(selectedPoint.x),
                              xIdentifier: selectedPoint.xIdentifier,
                              metadata: selectedPoint.metadata,
                          });
                      }
                    : undefined,
            },
            toolTipText: hasNextLevel
                ? undefined
                : `No ${nextLevelLabel} available`,
            icon: <ZoomInIcon />,
        });
    }

    if (effectivePointSelectActions.viewData) {
        menuItems.push({
            id: `${store.id}-point-view-data`,
            label: <StyledMenuLabel id="view-data">View Data</StyledMenuLabel>,
            props: {
                onClick: () => {
                    onClose();
                    drilldownStore.viewData();
                },
            },
            icon: <TableChartIcon />,
        });
    }

    if (effectivePointSelectActions.viewTopics) {
        menuItems.push({
            id: `${store.id}-point-view-topics`,
            label: <StyledMenuLabel>View Topics</StyledMenuLabel>,
            props: {
                onClick: () => {
                    onClose();
                    drilldownStore.viewTopics();
                },
            },
            icon: <JoinRightIcon />,
        });
    }

    if (effectivePointSelectActions.viewDrivers) {
        menuItems.push({
            id: `${store.id}-point-view-drivers`,
            label: (
                <StyledMenuLabel id="view-drivers">
                    View Drivers
                </StyledMenuLabel>
            ),
            props: {
                onClick: () => {
                    onClose();
                    drilldownStore.viewDrivers();
                },
            },
            icon: <JoinRightIcon />,
        });
    }

    if (effectivePointSelectActions.viewInConvos) {
        menuItems.push({
            id: `${store.id}-point-view-convos`,
            label: <StyledMenuLabel>View in Conversations</StyledMenuLabel>,
            props: {
                onClick: () => {
                    onClose();

                    drilldownStore.viewInConvos();

                    navigate("/app/conversations?fromDomain=signals");
                },
            },
            icon: <MessageIcon />,
        });
    }

    // Deprecated Topic data gives KBo nothing to analyze, so the action is dropped rather than
    // offered — unlike the zoom action, which stays visible to explain why a leaf row has no next
    // level. There is nothing here for the user to act on.
    if (effectivePointSelectActions.askKbo && !store.hasDeprecatedDataForKbo) {
        menuItems.push({
            id: `${store.id}-point-ask-kbo`,
            label: <StyledMenuLabel>Ask KBo</StyledMenuLabel>,
            props: {
                onClick: () => {
                    onClose();
                    drilldownStore.askKbo();
                },
            },
            icon: <AgentLogo gradient height="20px" width="20px" />,
        });
    }

    if (menuItems.length === 2) {
        // The only items must be the header (number) and the divider. Just remove the divider
        menuItems.splice(1, 1);
    }

    return (
        <AcxMenu
            anchorPosition={anchorPosition}
            open={open}
            onMenuClose={onClose}
            anchorReference="anchorPosition"
            menuItems={menuItems}
        />
    );
};

export default PointMenu;
